import { Link } from "react-router-dom";
import { ShieldCheck, FileText, Activity, ArrowRight, CheckCircle2, BookOpen, Siren, HeartPulse, Zap, Droplet, Wind } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { PublicHeader, PublicFooter } from "../components/PublicShell";
import { ShareButton } from "../components/ShareButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useSeo } from "../lib/seo";
import { PromoVideo, PROMO_VIDEO, videoLd } from "../components/PromoVideo";

const features = [
  { icon: Activity, title: "Clinical Decision Engine", desc: "Integrates medical conditions, medications, planned dental procedures, and relevant investigations to provide evidence-based clinical guidance." },
  { icon: FileText, title: "Medical Consultation & Referral", desc: "Generate a professional, pre-filled PDF with relevant patient information, clinical findings, and focused questions for the patient’s physician or specialist." },
  { icon: BookOpen, title: "Referenced Evidence Base", desc: "Recommendations are supported by relevant clinical guidelines and scientific references, with the supporting source available to the clinician." },
];

const modules = [
  "Anticoagulants & Antiplatelets",
  "Cardiac stents & DAPT timing",
  "Infective Endocarditis Prophylaxis",
  "Chronic steroids & adrenal insufficiency",
  "MRONJ / Antiresorptives",
  "Oncology — chemotherapy counts & radiation-related risk",
];

const popularGuides = [
  ["warfarin", "Warfarin (VKA)"], ["apixaban", "Apixaban (DOAC)"], ["clopidogrel", "Clopidogrel"], ["prosthetic-valve", "Prosthetic heart valve"],
  ["diabetes", "Diabetes mellitus"], ["pregnancy", "Pregnancy"], ["bisphosphonate-oral", "Oral bisphosphonates (MRONJ)"], ["hypertension", "Hypertension"],
];

const FAQ = [
  { q: "What is DentiCision?", a: "DentiCision is a clinical decision support platform for dentists. You enter the patient's medical conditions, medications, relevant lab results and the planned dental procedure, and it returns clear, referenced guidance — including what to do if the visit is an emergency and how to plan routine care." },
  { q: "Who is DentiCision for?", a: "General dentists, dental specialists, oral surgeons, dental students and hospital dental teams who treat medically complex patients — such as patients on anticoagulants, with cardiac conditions, diabetes, cancer therapy, transplants, or who are pregnant or breastfeeding." },
  { q: "Where do the recommendations come from?", a: "Every recommendation is rules-based and linked to international guidelines and references — including AHA, SDCEP, AAOMS, ACR, MASCC/ISOO, WFH, NICE, BNF and Little & Miller's Dental Management of the Medically Compromised Patient. No unverified AI-generated medical advice is used." },
  { q: "Does DentiCision replace my clinical judgment?", a: "No. DentiCision is an evidence-based second opinion that assists the dentist. It does not replace the treating dentist's professional clinical judgment or consultation with the patient's physician, and the treating dentist retains full clinical and legal responsibility." },
  { q: "How much does DentiCision cost?", a: "Every account starts with a 7-day free trial. After that it is USD 10 per month or USD 100 per year (two months free). You can cancel anytime and keep access until the end of your paid period." },
  { q: "Which conditions and medications are covered?", a: "More than 80 conditions and medications, including warfarin and DOACs, antiplatelets and stents, prosthetic valves and endocarditis risk, hypertension, heart failure, diabetes, steroids and adrenal insufficiency, bisphosphonates and denosumab (MRONJ), chemotherapy and radiotherapy, organ and bone-marrow transplants, biologics, pregnancy, breastfeeding and dental anxiety." },
  { q: "Can I generate referral letters and lab requests?", a: "Yes. DentiCision creates a professional medical consultation/referral PDF and a laboratory request PDF for the patient. When the patient returns with results, you enter them (with automatic unit conversion) and receive the final recommendation." },
  { q: "Does DentiCision help if a medical emergency happens in the chair?", a: "Yes. Every result shows the medical emergency protocols that match the patient's conditions and medications — for example seizure for epilepsy, hypoglycaemia for diabetes, adrenal crisis for long-term steroids — with numbered steps, drug doses and what not to do. A red Emergency button is available on every screen, and the protocols are printed in the case PDF. Sources: Resuscitation Council UK, ERC 2025, AHA 2025 and BNF." },
  { q: "Does it calculate local anaesthetic doses?", a: "Yes. The anaesthetic calculator gives the maximum safe dose and number of cartridges based on weight, anaesthetic and adrenaline concentration, with cardiac and steroid-related cautions." },
  { q: "Is my patients' data private?", a: "Cases are stored privately in your own account and are never shown publicly. Public clinical guides contain general information only — no patient data." },
];

export default function Landing() {
  const { user } = useAuth();
  useSeo({
    title: "DentiCision — Clinical Decision Support for Dentists | Medically Complex Patients",
    description: "Evidence-based clinical decision support for dentists treating medically complex patients — anticoagulants, cardiac, diabetes, pregnancy, MRONJ, transplants and 80+ conditions & medications, with referenced emergency and routine guidance.",
    path: "/",
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
      videoLd("DentiCision — Clinical decision support for dentists", "See how dentists use DentiCision to make confident, evidence-based decisions for medically complex patients.", PROMO_VIDEO, "PT1M0S", "/"),
    ] },
  });
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#16B5A6]/10 text-[#0B7569] text-xs font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" /> Evidence-Based · Referenced · Clinician-Focused
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2545] mt-5 leading-[1.1]">
            Confident dental decisions for medically complex patients
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mt-5 leading-relaxed">
            Bring together your patient’s medical conditions, medications, planned dental procedure, and relevant
            investigations. DentiCision provides evidence-based guidance to support a clear, informed clinical decision.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={user ? "/app" : "/register"}>
              <Button data-testid="hero-cta-btn" size="lg" className="bg-[#1C6FE3] hover:bg-[#1559c0] h-12 px-7 text-base">
                {user ? "Open the app" : "Start free trial"} <ArrowRight className="w-4.5 h-4.5 ml-2" />
              </Button>
            </Link>
            <Link to={user ? "/app/account" : "/pricing"}>
              <Button variant="outline" size="lg" data-testid="hero-subscribe-btn" className="h-12 px-7 text-base border-slate-300">Subscribe now</Button>
            </Link>
          </div>
          <p data-testid="hero-pricing-line" className="text-sm text-slate-600 mt-3">
            <b className="text-slate-900">$10/month</b> · <b className="text-slate-900">$100/year</b> · 7-day free trial · Cancel anytime ·{" "}
            <Link to="/guides" data-testid="hero-guides-btn" className="text-[#1C6FE3] font-medium hover:underline whitespace-nowrap">Free clinical guides →</Link>
          </p>
          <p data-testid="hero-disclaimer" className="text-xs text-slate-500 mt-4 max-w-xl">DentiCision provides an evidence-based <b>second opinion</b> to assist the dentist — it does not replace professional clinical judgment or consultation with the patient's physician. <Link to="/medical-disclaimer" className="underline">Medical Disclaimer</Link></p>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-[#16B5A6]/10 rounded-3xl -rotate-2" />
          <img
            src="https://images.unsplash.com/photo-1588776814546-daab30f310ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
            alt="Clinician reviewing a medically complex patient's plan"
            className="relative rounded-2xl shadow-xl w-full h-[380px] object-cover"
          />
        </div>
      </section>

      <PromoVideo />

      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B2545] text-center">From clinical complexity to a clear decision</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-[#1C6FE3]/10 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-[#1C6FE3]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#0B2545] mt-4">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B2545]">Comprehensive Clinical Modules</h2>
          <p className="text-slate-600 mt-3">Focused guidance for medically complex scenarios encountered in everyday dental practice.</p>
          <ul className="mt-6 space-y-3">
            {modules.map((m) => (
              <li key={m} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#16B5A6] shrink-0 mt-0.5" />
                <span className="text-slate-800">{m}</span>
              </li>
            ))}
          </ul>
        </div>
        <img
          src="https://images.pexels.com/photos/8413334/pexels-photo-8413334.jpeg?auto=compress&cs=tinysrgb&w=1000"
          alt="Clinical examination"
          className="rounded-2xl shadow-lg w-full h-[360px] object-cover"
        />
      </section>

      <section className="bg-[#0B2545] text-white py-16" data-testid="landing-emergency">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/20 text-red-200 text-xs font-bold"><Siren className="w-3.5 h-3.5" /> Medical emergency readiness</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-4">Ready if something goes wrong in the chair</h2>
            <p className="text-slate-300 mt-3 leading-relaxed">Each recommendation comes with the medical emergency protocols that match the patient: what to recognise, numbered steps, exact drug doses for adults and children, and what <b>not</b> to do. A red <b>Emergency</b> button stays on every screen, and the protocols print in the case report.</p>
            <ul className="mt-5 space-y-2 text-sm text-slate-200">
              {["Matched to the patient — epilepsy → seizure, diabetes → hypoglycaemia, steroids → adrenal crisis", "12 protocols: cardiac arrest, anaphylaxis, fainting, hypoglycaemia, seizure, asthma, chest pain, stroke, adrenal crisis, choking, bleeding, LA toxicity", "Referenced to Resuscitation Council UK, ERC 2025, AHA 2025 and BNF"].map((t) => (
                <li key={t} className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#16B5A6] shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
            <Link to="/emergency" data-testid="landing-emergency-link" className="inline-flex items-center gap-2 mt-7 rounded-full bg-red-600 hover:bg-red-700 px-6 h-11 font-semibold transition-colors"><Siren className="w-4 h-4" /> See the emergency protocols</Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[[HeartPulse, "Cardiac arrest", "CPR 30:2 + AED"], [Zap, "Anaphylaxis", "Adrenaline 500 mcg IM"], [Droplet, "Hypoglycaemia", "Glucose / glucagon 1 mg"], [Wind, "Acute asthma", "Salbutamol via spacer"]].map(([Icon, t, d]) => (
              <div key={t} className="rounded-xl bg-white/5 border border-white/10 p-4">
                <Icon className="w-5 h-5 text-red-400" />
                <p className="font-heading font-semibold mt-3">{t}</p>
                <p className="text-xs font-bold text-red-300 mt-1">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200 py-16" data-testid="landing-guides">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B2545]">Free Clinical Guides</h2>
              <p className="text-slate-600 mt-2">Referenced emergency & routine management for 80+ conditions and medications.</p>
            </div>
            <Link to="/guides" data-testid="landing-all-guides-link" className="text-sm font-semibold text-[#1C6FE3] inline-flex items-center gap-1">Browse all guides <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {popularGuides.map(([slug, name]) => (
              <Link key={slug} to={`/guides/${slug}`} className="rounded-xl bg-white border border-slate-200 p-4 hover:border-[#1C6FE3] transition-colors">
                <p className="text-xs text-slate-500">Dental management</p>
                <p className="font-medium text-[#0B2545] mt-0.5">{name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="landing-faq">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B2545] text-center">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="mt-8">
          {FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`}>
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#0B2545] font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="flex justify-center mt-10">
          <ShareButton label="Share DentiCision with a colleague" testid="landing-share-btn" />
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
